import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Lock, Plug, RotateCw, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { ragApi } from '@/api/rag';
import { unwrapError } from '@/api/client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UploadCard } from '@/components/rag/UploadCard';
import { DocumentsTable } from '@/components/rag/DocumentsTable';
import { IngestionCard } from '@/components/rag/IngestionCard';

export function RAG(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const kb = useQuery({
    queryKey: ['rag', 'kb'],
    queryFn: () => ragApi.getKnowledgeBase(),
    refetchInterval: (q) => {
      const data = q.state.data;
      return data?.configured && data.status === 'provisioning' ? 5000 : false;
    },
  });

  const retry = useMutation({
    mutationFn: () => ragApi.retryKnowledgeBase(),
    onSuccess: () => {
      toast.success(t('rag.kb.retryToast'));
      void queryClient.invalidateQueries({ queryKey: ['rag', 'kb'] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const info = kb.data;
  const ready = !!info?.configured && info.enabled && info.status === 'active';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('rag.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('rag.subtitle')}</p>
        </div>
        {info?.configured && info.knowledgeBaseId ? (
          <Badge variant="secondary" className="font-mono text-xs">
            KB {info.knowledgeBaseId}
          </Badge>
        ) : null}
      </header>

      {kb.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('common.loading')}
        </div>
      ) : kb.error ? (
        <p className="text-sm text-destructive">{unwrapError(kb.error)}</p>
      ) : !info?.configured ? (
        info && !info.envConfigured ? (
          <Alert variant="destructive">
            <Settings2 className="h-4 w-4" />
            <AlertTitle>{t('rag.envDisabledTitle')}</AlertTitle>
            <AlertDescription>{t('rag.envDisabledBody')}</AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <Plug className="h-4 w-4" />
            <AlertTitle>{t('rag.notConfiguredTitle')}</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{t('rag.notConfiguredBody')}</p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/integrations">{t('rag.goToIntegrations')}</Link>
              </Button>
            </AlertDescription>
          </Alert>
        )
      ) : info.status === 'provisioning' ? (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertTitle>{t('rag.kb.provisioningTitle')}</AlertTitle>
          <AlertDescription>{t('rag.kb.provisioningBody')}</AlertDescription>
        </Alert>
      ) : info.status === 'failed' ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('rag.kb.failedTitle')}</AlertTitle>
          <AlertDescription className="space-y-2">
            {info.error ? <p className="font-mono text-xs">{info.error}</p> : null}
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => retry.mutate()}
              disabled={retry.isPending}
            >
              {retry.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RotateCw className="h-3 w-3" />
              )}
              {t('rag.kb.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : !info.enabled ? (
        <Alert variant="warning">
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('rag.runtimeDisabledTitle')}</AlertTitle>
          <AlertDescription>
            <p>
              {t('rag.runtimeDisabledBodyNew')}{' '}
              <Link to="/integrations" className="underline">
                {t('rag.goToIntegrations')}
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      ) : null}

      {ready ? (
        <>
          <UploadCard />
          <DocumentsTable />
          <IngestionCard />
        </>
      ) : null}
    </div>
  );
}
